import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  KeyRound, 
  CheckCircle, 
  Loader2, 
  Building2, 
  Shield, 
  Sparkles, 
  Crown,
  ArrowRight,
  AlertCircle
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GYANODAYA_LOGO = 'https://static.prod-images.emergentagent.com/jobs/2d9de157-6aba-4791-b4d7-cfe9f78303cb/images/caa72923253f39d305789ea60b60a4f20b404a1e2c8d1c106a92bfb7211cd8c8.png';

const EDITION_ICONS = {
  standard: Shield,
  professional: Sparkles,
  enterprise: Crown,
};

const EDITION_COLORS = {
  standard: 'bg-slate-500',
  professional: 'bg-blue-500',
  enterprise: 'bg-purple-500',
};

const LicenseActivation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const siteId = searchParams.get('site') || searchParams.get('site_id');
  const [licenseKey, setLicenseKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [activationResult, setActivationResult] = useState(null);
  const [error, setError] = useState('');
  const [tenantBranding, setTenantBranding] = useState(null);

  // Fetch tenant branding if site_id is provided
  useEffect(() => {
    const fetchTenantBranding = async () => {
      if (siteId) {
        try {
          const response = await axios.get(`${API_URL}/oem/tenant-by-site/${siteId}`);
          if (response.data) {
            setTenantBranding({
              companyName: response.data.company_name,
              logo: response.data.branding?.logo_url,
              primaryColor: response.data.branding?.primary_color || '#7c3aed',
              appName: response.data.branding?.app_name || response.data.company_name,
            });
          }
        } catch (err) {
          console.log('Tenant not found for site_id:', siteId);
        }
      }
    };
    fetchTenantBranding();
  }, [siteId]);

  // Pre-fill from URL if key provided
  useEffect(() => {
    const keyFromUrl = searchParams.get('key');
    if (keyFromUrl) {
      setLicenseKey(keyFromUrl.toUpperCase());
      checkLicenseInfo(keyFromUrl);
    }
  }, [searchParams]);

  const formatLicenseKey = (value) => {
    // Remove all non-alphanumeric characters
    const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    // Split into groups of 4
    const groups = cleaned.match(/.{1,4}/g) || [];
    return groups.slice(0, 4).join('-');
  };

  const handleKeyChange = (e) => {
    const formatted = formatLicenseKey(e.target.value);
    setLicenseKey(formatted);
    setError('');
    setLicenseInfo(null);
    
    // Auto-check when complete key is entered
    if (formatted.length === 19) { // XXXX-XXXX-XXXX-XXXX
      checkLicenseInfo(formatted);
    }
  };

  const checkLicenseInfo = async (key) => {
    setChecking(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/oem/license-info/${key}`);
      setLicenseInfo(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setError('Invalid license key. Please check and try again.');
      } else {
        setError('Failed to verify license key');
      }
      setLicenseInfo(null);
    } finally {
      setChecking(false);
    }
  };

  const handleActivate = async () => {
    if (!licenseKey || licenseKey.length !== 19) {
      toast.error('Please enter a valid license key');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/oem/activate-license`, {
        license_key: licenseKey
      });
      
      setActivationResult(response.data);
      
      if (response.data.already_activated) {
        toast.info('License already activated. You can proceed to login.');
      } else {
        toast.success('License activated successfully!');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to activate license');
      toast.error(error.response?.data?.detail || 'Activation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToLogin = () => {
    // Navigate to login with site_id if available (either from URL or from activation result)
    const targetSiteId = siteId || activationResult?.site_id;
    if (targetSiteId) {
      navigate(`/login?site=${targetSiteId}`);
    } else {
      navigate('/login');
    }
  };

  // Determine which logo and name to display
  const displayLogo = tenantBranding?.logo || GYANODAYA_LOGO;
  const displayName = tenantBranding?.appName || 'Gyanodaya';
  const primaryColor = tenantBranding?.primaryColor || '#7c3aed';

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(135deg, ${primaryColor}33 0%, #0f172a 50%, ${primaryColor}22 100%)`
      }}
    >
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={displayLogo} alt={displayName} className="h-16 w-16 mx-auto mb-4 rounded-lg" />
          <h1 className="text-3xl font-bold text-white">{displayName}</h1>
          <p className="text-purple-200 mt-1">License Activation</p>
          {tenantBranding?.companyName && tenantBranding.companyName !== displayName && (
            <p className="text-purple-300/70 text-sm mt-2">for {tenantBranding.companyName}</p>
          )}
        </div>

        {/* Activation Card */}
        {!activationResult ? (
          <Card className="border-purple-500/20 bg-white/95 backdrop-blur">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-2">
                <KeyRound className="w-6 h-6 text-purple-600" />
              </div>
              <CardTitle>Activate Your License</CardTitle>
              <CardDescription>
                Enter the license key from your welcome email to get started
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* License Key Input */}
              <div className="space-y-2">
                <Label>License Key</Label>
                <Input
                  value={licenseKey}
                  onChange={handleKeyChange}
                  placeholder="XXXX-XXXX-XXXX-XXXX"
                  className="text-center text-lg font-mono tracking-wider"
                  maxLength={19}
                  data-testid="license-key-input"
                />
                {checking && (
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verifying license...
                  </p>
                )}
                {error && (
                  <p className="text-sm text-red-500 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </p>
                )}
              </div>

              {/* License Info Preview */}
              {licenseInfo && (
                <Card className="bg-muted/50">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{licenseInfo.company_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {licenseInfo.is_activated ? 'Already Activated' : 'Ready to Activate'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {(() => {
                        const EditionIcon = EDITION_ICONS[licenseInfo.edition] || Shield;
                        return (
                          <Badge className={`${EDITION_COLORS[licenseInfo.edition]} text-white`}>
                            <EditionIcon className="w-3 h-3 mr-1" />
                            {licenseInfo.edition?.charAt(0).toUpperCase() + licenseInfo.edition?.slice(1)} Edition
                          </Badge>
                        );
                      })()}
                    </div>
                    
                    <div className="text-sm space-y-1 pt-2 border-t">
                      <p>
                        <span className="text-muted-foreground">Creator Licenses:</span>{' '}
                        <span className="font-medium">{licenseInfo.creator_licenses}</span>
                      </p>
                      <p>
                        <span className="text-muted-foreground">Viewer Licenses:</span>{' '}
                        <span className="font-medium">{licenseInfo.viewer_licenses}</span>
                      </p>
                      <p>
                        <span className="text-muted-foreground">Valid Until:</span>{' '}
                        <span className="font-medium">
                          {licenseInfo.valid_until ? new Date(licenseInfo.valid_until).toLocaleDateString() : 'N/A'}
                        </span>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Activate Button */}
              <Button 
                onClick={handleActivate} 
                disabled={loading || !licenseInfo || licenseKey.length !== 19}
                className="w-full"
                style={{ backgroundColor: primaryColor }}
                data-testid="activate-license-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <KeyRound className="w-4 h-4 mr-2" />
                )}
                {licenseInfo?.is_activated ? 'Proceed to Login' : 'Activate License'}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Already activated?{' '}
                <button 
                  onClick={() => siteId ? navigate(`/login?site=${siteId}`) : navigate('/login')} 
                  className="hover:underline"
                  style={{ color: primaryColor }}
                >
                  Go to Login
                </button>
              </p>
            </CardContent>
          </Card>
        ) : (
          /* Success Card */
          <Card className="border-green-500/20 bg-white/95 backdrop-blur">
            <CardContent className="p-8 text-center space-y-6">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              
              <div>
                <h2 className="text-2xl font-bold text-green-600">
                  {activationResult.already_activated ? 'Already Activated!' : 'Activation Successful!'}
                </h2>
                <p className="text-muted-foreground mt-2">
                  {activationResult.message}
                </p>
              </div>

              <Card className="bg-muted/50">
                <CardContent className="p-4 space-y-2 text-left">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Company:</span>
                    <span className="font-medium">{activationResult.company_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Site ID:</span>
                    <code className="bg-background px-2 py-0.5 rounded text-sm">{activationResult.site_id}</code>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Edition:</span>
                    {(() => {
                      const EditionIcon = EDITION_ICONS[activationResult.edition] || Shield;
                      return (
                        <Badge className={`${EDITION_COLORS[activationResult.edition]} text-white`}>
                          <EditionIcon className="w-3 h-3 mr-1" />
                          {activationResult.edition?.charAt(0).toUpperCase() + activationResult.edition?.slice(1)}
                        </Badge>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>

              {!activationResult.already_activated && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
                  <p className="text-amber-800 text-sm">
                    <strong>Default Password:</strong> Welcome@123
                    <br />
                    <span className="text-amber-600">You will be asked to change this on first login.</span>
                  </p>
                </div>
              )}

              <Button 
                onClick={handleProceedToLogin} 
                className="w-full"
                style={{ backgroundColor: primaryColor }}
                data-testid="proceed-to-login-btn"
              >
                Proceed to Login
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-purple-300/60 text-sm mt-8">
          © 2025 {displayName} | Powered by ScatterPie Analytics
        </p>
      </div>
    </div>
  );
};

export default LicenseActivation;
